import { useRef, useState } from "react";
import { Button } from "./ui/button";
import { Loader2, Upload, X, UserRound } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 5 * 1024 * 1024;
const OK_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Optional profile photo uploader. Calls onChange(publicUrl) once stored.
export const PhotoUpload = ({ value = "", onChange, testId = "photo-upload" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!OK_TYPES.includes(file.type)) { toast.error("Please choose a JPG, PNG or WEBP image."); return; }
    if (file.size > MAX_BYTES) { toast.error("Image must be under 5MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/upload/photo`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      onChange(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err.message || "Could not upload the photo.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid={testId}>
      <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-50">
        {value ? (
          <img src={value} alt="Profile preview" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <UserRound className="h-7 w-7 text-slate-300" />
        )}
      </div>
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} data-testid={`${testId}-input`} />
        <Button type="button" variant="outline" size="sm" onClick={pick} disabled={uploading} data-testid={`${testId}-button`}>
          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {value ? "Change photo" : "Upload photo"}
        </Button>
        {value && !uploading && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testId}-remove`}>
            <X className="mr-1 h-4 w-4" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
};
